import * as React from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {Checkbox} from 'shells/checkbox/checkbox';
import {Dialog} from 'shells/dialog/dialog';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {FooterBar} from 'shells/modal/footer-bar';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import styles from './ready-for-sending-page.module.css';

type Props = {
    onClickBack: () => void;
    onClickConfirmSend: (
        shouldIncludeAlreadyReceived: boolean,
        isDraft?: boolean,
        editionName?: string
    ) => void;
    isSending: boolean;
    errorSending?: string;
    clearErrorSending: () => void;
    numAlreadyReceived: number | null | undefined;
    numRecipients: number;
    outreachItemType: string;
    entityTypeNoun: string;
    isDraft: boolean;
    outreachName: string;
};

export function ReadyForSendingPageFooter(props: Props) {
    const {
        onClickBack,
        onClickConfirmSend,
        isSending,
        errorSending,
        clearErrorSending,
        numAlreadyReceived,
        numRecipients,
        outreachItemType,
        entityTypeNoun,
        isDraft,
        outreachName,
    } = props;

    // Including those who have already received the outreach is false by default
    const [shouldIncludeAlreadyReceived, setShouldIncludeAlreadyReceived] =
        React.useState<boolean>(false);
    const [shouldShowConfirmation, setShouldShowConfirmation] = React.useState<boolean>(false);
    const [shouldShowNameEditionModal, setShouldShowNameEditionModal] =
        React.useState<boolean>(false);
    const saveButtonRef = React.useRef();

    const isDripSequence = outreachItemType === 'DRIP_SEQUENCE';

    // The number of recipients will always be 2 or more (otherwise we couldn't even be here)
    let saveButtonText = isDraft
        ? `Send to ${numRecipients} ${entityTypeNoun}`
        : `Duplicate & send to ${numRecipients} ${entityTypeNoun}`;

    if (isDripSequence) {
        saveButtonText = `Add ${numRecipients} ${entityTypeNoun} to drip sequence`;
    }

    function handleOnClickPrimary() {
        // If this is duplicating and sending, then we need to show the modal to name the duplicate edition
        if (isDraft) {
            // For drafts, this is the final step of confirmation
            onClickConfirmSend(shouldIncludeAlreadyReceived, isDraft);
        } else if (isDripSequence) {
            // For drip sequences, this is the final step of confirmation
            onClickConfirmSend(shouldIncludeAlreadyReceived);
        } else {
            // We are duplicating an existing edition, and need to show the name edition modal
            setShouldShowNameEditionModal(true);
        }

        // Close this confirmation
        setShouldShowConfirmation(false);
    }

    return (
        <>
            {errorSending ? (
                <div className={styles['error-banner-container']}>
                    <FlashBanner
                        type='error'
                        hasBorder={true}
                        leftAlignContent={true}
                        onHidden={clearErrorSending}
                    >
                        <div>{errorSending}</div>
                    </FlashBanner>
                </div>
            ) : undefined}
            <FooterBar
                left={
                    <ButtonMover size='big' direction='left'>
                        <Button onClick={onClickBack} variant='text-secondary' size='big'>
                            Back
                        </Button>
                    </ButtonMover>
                }
                right={
                    <div className={styles['footer-right-container']}>
                        {numAlreadyReceived ? (
                            <Checkbox
                                label={`Include ${entityTypeNoun} who have already received this ${
                                    isDripSequence ? 'drip sequence' : 'message'
                                }`}
                                checked={shouldIncludeAlreadyReceived}
                                onChange={(isChecked) => setShouldIncludeAlreadyReceived(isChecked)}
                            />
                        ) : undefined}
                        <SaveButton
                            // @ts-expect-error
                            onClick={() => {
                                setShouldShowConfirmation(true);
                            }}
                            getButtonRef={saveButtonRef}
                            buttonText={saveButtonText}
                            savingText={isDripSequence ? 'Adding…' : 'Sending…'}
                            isSaving={isSending}
                            errorSaving={Boolean(errorSending)}
                            disabled={isSending || Boolean(errorSending)}
                        />
                    </div>
                }
            />
            {shouldShowConfirmation ? (
                <Dialog
                    anchor={saveButtonRef.current}
                    question={getDialogQuestion(
                        numRecipients,
                        entityTypeNoun,
                        isDraft,
                        isDripSequence ? outreachName : undefined
                    )}
                    primaryText={`Yes, ${isDripSequence ? 'add' : 'send'} now`}
                    primaryVariant='save'
                    onPrimary={handleOnClickPrimary}
                    secondaryText={`No, don’t ${isDripSequence ? 'add' : 'send'} `}
                    onSecondary={() => setShouldShowConfirmation(false)}
                />
            ) : undefined}
            {shouldShowNameEditionModal ? (
                <SingleInputModal
                    title={`Name your ${
                        outreachItemType === 'BROADCAST' ? 'broadcast' : 'edition'
                    }`}
                    subtitle='Add a descriptive name so you can find it again later'
                    placeholder={`Copy of ${outreachName}`}
                    initialValue={`Copy of ${outreachName}`}
                    buttonText='Create'
                    requireValue={true}
                    onClose={() => {
                        setShouldShowNameEditionModal(false);
                    }}
                    isSaving={isSending}
                    errorMessage={errorSending}
                    onSave={(inputValue) => {
                        onClickConfirmSend(shouldIncludeAlreadyReceived, false, inputValue);
                        setShouldShowNameEditionModal(false);
                    }}
                />
            ) : undefined}
        </>
    );
}

function getDialogQuestion(
    numRecipients: number,
    entityTypeNoun: string,
    isDraft: boolean,
    dripSequenceName?: string | null
): string {
    // Only editions can also be drafts
    if (isDraft) {
        return `This message will be sent to ${numRecipients} ${entityTypeNoun}. Would you like to send this now?`;
    } else if (dripSequenceName) {
        // If passed a drip sequence name ... then it is a drip sequence
        return `This will add ${numRecipients} ${entityTypeNoun} to ${dripSequenceName}. Would you like to do this now?`;
    } else {
        // This is an already sent edition
        return `This message will be copied and sent to ${numRecipients} ${entityTypeNoun}. Are you sure you’d like to send this now?`;
    }
}
